/**
 * Privacy case file — the PDF ready to hand a REGULATOR.
 *
 * Layout doctrine (operator order 2026-08-20): paragraphs, not tables. Each
 * item reads as prose under its own heading — Factor 1, then its explanation
 * below — in the organization's own Deck Studio colors, so the document
 * leaves the building looking like the client's, never like a dashboard.
 *
 * CONTENT RULES — regulator scope
 *
 * IN: the incident narrative (what happened, who was affected, what
 * information was involved, root cause), the HIPAA four-factor breach risk
 * assessment with each factor's explanation, the determination and who
 * approved it, jurisdictions and reporting deadlines, mitigation and
 * corrective actions, notifications made, sanctions applied, and education
 * delivered. That is the story a regulator is owed.
 *
 * OUT — noise and exposure, deliberately:
 *   - the internal case timeline, interview notes, the document inventory,
 *     and the case team roster (internal work product; invites requests)
 *   - numeric scoring (factor scores, combined score) — the qualitative
 *     four-factor analysis is the regulatory standard; internal arithmetic
 *     invites cross-examination of methodology
 *   - approver commentary, jurisdiction notes, sanction descriptions
 *     (may name employees), and any "open questions" scaffolding
 *   - any mention of agents or AI. Assessments appear as the organization's
 *     own work product, because that is what they are once adopted.
 *
 * Precision Compliance Group appears once, small, as preparer of the format —
 * never as a party to the case.
 */

import { jsPDF } from 'jspdf';

// ── Palette — the client's own Deck Studio theme, so every organization's
//    case file leaves in its colors. The slate-and-navy set below is the
//    fallback when no branding is on file. ─────────────────────────────────
const INK = [30, 41, 59];        // slate-800
const INK_MID = [71, 85, 105];   // slate-600
const INK_SOFT = [100, 116, 139];// slate-500
const RULE = [226, 232, 240];    // slate-200
const PANEL = [248, 250, 252];   // slate-50
const NAVY = [30, 58, 138];      // blue-900 — the fallback accent
const WHITE = [255, 255, 255];

const hexToRgb = (hex, fallback) => {
  const s = String(hex || '').replace(/^#/, '').trim();
  if (!/^[0-9A-Fa-f]{6}$/.test(s)) return fallback;
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
};

// The client's Deck Studio tokens mapped onto this document's roles. Any
// missing or malformed token falls back to the counsel palette above.
export function paletteFromBranding(tokens) {
  const t = tokens || {};
  return {
    ink: hexToRgb(t.ink, INK),
    inkMid: INK_MID,
    inkSoft: hexToRgb(t.muted, INK_SOFT),
    rule: RULE,
    panel: hexToRgb(t.surface, PANEL),
    accent: hexToRgb(t.brand, NAVY),
    white: WHITE,
  };
}

const NA = 'None on file';
const MARGIN = 52;

const has = (v) => v != null && String(v).trim() !== '';
const na = (v) => (has(v) ? String(v).trim() : NA);

// snake_case values read as raw database tokens; a regulator gets words.
const humanize = (v) => (has(v) ? String(v).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '');

/**
 * Strip system authorship and internal scaffolding from stored text.
 *
 * The assessor's notes carry lines like "Agent read. Likelihood: high," and
 * "Recorded by the privacy agent on 2026-07-30 UTC," plus "Still needed:"
 * blocks listing what the team had not yet gathered. None of that belongs in
 * a document a regulator reads: provenance is internal, and an inventory of
 * open gaps is exposure, not substance. The whole trailing block goes.
 */
export const scrub = (v) => {
  if (!has(v)) return '';
  return String(v)
    .replace(/Agent read\.?[^\n]*\n?/gi, '')
    .replace(/Recorded by the privacy agent[^.\n]*\.?\s*/gi, '')
    .replace(/Summary of this analysis,[^\n]*\n?/gi, '')
    .replace(/\bthe privacy agent\b/gi, 'the privacy team')
    .replace(/\bprivacy agent\b/gi, 'privacy team')
    .replace(/(Open questions:|Still needed:)[\s\S]*$/i, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Make pasted document text readable as flowing paragraphs.
 *
 * Text often arrives pasted straight out of a PDF: hard line breaks
 * mid-sentence, Symbol-font bullets that render as mojibake, and page
 * furniture. This normalizes it: private-use and control characters go,
 * single newlines inside a sentence become spaces, and real structure
 * (blank lines, bullets, numbered items) is kept.
 */
export const cleanUpdateText = (v) => {
  if (!has(v)) return '';
  const lines = String(v)
    .replace(/\r\n?/g, '\n')
    .replace(/[\uF0B7\uF0A7\uF0D8]/g, '\u2022') // Symbol-font bullets
    .replace(/[\uE000-\uF8FF\uFFFD]/g, '')      // private-use glyphs, replacement char
    .replace(/\s*%[\u00CC-\u00CF]\s*/g, ' \u00B7 ') // UTF-8-mangled separators from pasted PDFs
    // Geometric shapes (U+25CF et al.) sit outside jsPDF's WinAnsi range;
    // the encoder mangles each into two Latin-1 bytes. A WinAnsi-safe
    // middle dot preserves the meaning.
    .replace(/\s*[\u25A0-\u25FF\u2B24]\s*/g, ' \u00B7 ')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .split('\n').map((l) => l.trim());
  const out = [];
  for (const line of lines) {
    const prev = out.length ? out[out.length - 1] : null;
    const isStructural = line === '' || /^([\u2022\-\u2013\u2014*]|\d+[.)]\s)/.test(line);
    const prevOpen = prev != null && prev !== '' && !/[.:;!?\u2022]$/.test(prev) && !/^([\u2022\-\u2013\u2014*]|\d+[.)]\s)/.test(line);
    if (prev != null && !isStructural && prevOpen) out[out.length - 1] = `${prev} ${line}`;
    else out.push(line);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').replace(/[ \t]{2,}/g, ' ').trim();
};

// Kept for callers and pins: bounded prose with a pointer to the record.
const TIMELINE_CLAMP = 700;
export const clampEntry = (v, limit = TIMELINE_CLAMP) => {
  const s = String(v || '');
  if (s.length <= limit) return s;
  const cut = s.slice(0, limit);
  const atWord = cut.slice(0, Math.max(cut.lastIndexOf(' '), limit - 80));
  return `${atWord.replace(/[\s,;:.·•-]+$/, '')} … Full text is on file in the case record.`;
};

// UTC-safe date rendering — a date must never shift a day by timezone.
const fmtDate = (v) => {
  if (!v) return NA;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return na(v);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

const joinBits = (bits) => bits.filter((b) => b != null && String(b).trim() !== '').join(' · ') || NA;

const STATUS_LABELS = {
  draft: 'Draft',
  under_investigation: 'Under investigation',
  risk_assessment_complete: 'Risk assessment complete',
  determination_pending_approval: 'Determination pending approval',
  approved_not_breach: 'Determined — not a breach',
  approved_breach: 'Determined — breach',
  notification_complete: 'Notification complete',
  closed: 'Closed',
};

// The notification deadline only exists for a breach. A case determined NOT
// a breach has no notification duty, and printing a date (or "not yet set")
// on it invites a regulator to ask why a clock appears on a no-notify file:
// it reads N/A. A determined breach shows the date. An undetermined case
// shows the date when one is computed, otherwise says it is not yet set.
export function notificationDeadlineDisplay(inc) {
  const approvals = Array.isArray(inc.approvals) ? inc.approvals : [];
  const latestApproval = approvals.length ? approvals[approvals.length - 1] : null;
  const notBreach = inc.status === 'approved_not_breach'
    || (inc.status === 'closed' && latestApproval?.determination_approved === 'approved_not_breach');
  if (notBreach) return 'N/A — determined not a breach';
  return has(inc.notification_deadline_date) ? fmtDate(inc.notification_deadline_date) : 'Not yet set';
}

// ---------------------------------------------------------------------------
// Model — the regulator-scoped picture of the case, renderer-independent.
// ---------------------------------------------------------------------------
export function buildCaseFileModel({ incident, orgName = '', logoDataUrl = null, brandTokens = null }) {
  const inc = incident || {};
  const arr = (x) => (Array.isArray(x) ? x : []);

  const ra = inc.current_risk_assessment || arr(inc.risk_assessments)[0] || null;

  // When the case-level count is not yet set, the per-state counts are the
  // best number on file — 654 Florida residents is an answer, not a blank.
  const stateCount = arr(inc.affected_states)
    .reduce((sum, s) => sum + (Number(s.residents_affected) || 0), 0);
  const affectedCount = inc.number_affected != null
    ? String(inc.number_affected)
    : (stateCount > 0 ? String(stateCount) : null);

  const prose = (v) => cleanUpdateText(scrub(v));

  return {
    orgName: orgName || '',
    logoDataUrl,
    brandTokens,
    title: inc.title || 'Privacy incident',
    caseRef: inc.id ? String(inc.id).slice(0, 8).toUpperCase() : '',
    status: STATUS_LABELS[inc.status] || na(inc.status),
    generatedAt: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),

    glance: [
      { label: 'Status', value: STATUS_LABELS[inc.status] || humanize(inc.status) },
      has(inc.incident_known_date || inc.discovery_date) && { label: 'First known', value: fmtDate(inc.incident_known_date || inc.discovery_date) },
      affectedCount && { label: 'Individuals affected', value: affectedCount },
      { label: 'Notification deadline', value: notificationDeadlineDisplay(inc) },
    ].filter(Boolean).slice(0, 4),

    // The narrative — each block a heading with prose below it.
    narrative: [
      has(inc.summary) && { label: 'Summary', text: prose(inc.summary) },
      has(inc.facts) && { label: 'What happened', text: prose(inc.facts) },
      has(inc.who_impacted) && { label: 'Who was affected', text: prose(inc.who_impacted) },
      has(inc.phi_involved) && { label: 'Information involved', text: prose(inc.phi_involved) },
      (has(inc.root_cause_category) || has(inc.root_cause_notes))
        && { label: 'Root cause', text: joinBits([humanize(inc.root_cause_category), prose(inc.root_cause_notes)]) },
    ].filter(Boolean),

    // Short facts render as aligned label/value lines, not a table grid.
    keyFacts: [
      has(inc.incident_known_date || inc.discovery_date) && ['Incident first known', fmtDate(inc.incident_known_date || inc.discovery_date)],
      has(inc.breach_discovered_date) && ['Breach discovered — 45 CFR 164.404(a)(2) clock start', fmtDate(inc.breach_discovered_date)],
      has(inc.breach_determined_date) && ['Determination approved', fmtDate(inc.breach_determined_date)],
      ['Notification deadline', notificationDeadlineDisplay(inc)],
      has(inc.state_deadline_date) && ['State reporting deadline', fmtDate(inc.state_deadline_date)],
      affectedCount && ['Individuals affected', affectedCount],
      ['Business associate involved', inc.business_associate_involved ? 'Yes' : 'No'],
    ].filter(Boolean),

    // The four-factor assessment: heading, qualitative assessment line, then
    // the explanation as a paragraph. No numeric scores — the qualitative
    // analysis is the regulatory standard.
    risk: ra ? {
      conclusion: [
        has(ra.determination) && ['Determination', humanize(ra.determination)],
        has(ra.likelihood_overall) && ['Likelihood of compromise', humanize(ra.likelihood_overall)],
        has(ra.impact_overall) && ['Impact', humanize(ra.impact_overall)],
        (has(ra.exception_applied) || has(ra.exception_notes)) && ['Exception considered', joinBits([ra.exception_applied, prose(ra.exception_notes)])],
      ].filter(Boolean),
      factors: [
        { title: 'Factor 1 — Nature and extent of the PHI involved', assessment: joinBits([has(ra.factor1_likelihood) && `Likelihood: ${humanize(ra.factor1_likelihood)}`, has(ra.factor1_impact) && `Impact: ${humanize(ra.factor1_impact)}`]), text: prose(ra.factor1_notes) },
        { title: 'Factor 2 — The unauthorized person who used or received the PHI', assessment: joinBits([has(ra.factor2_likelihood) && `Likelihood: ${humanize(ra.factor2_likelihood)}`, has(ra.factor2_impact) && `Impact: ${humanize(ra.factor2_impact)}`]), text: prose(ra.factor2_notes) },
        { title: 'Factor 3 — Whether the PHI was actually acquired or viewed', assessment: joinBits([has(ra.factor3_likelihood) && `Likelihood: ${humanize(ra.factor3_likelihood)}`, has(ra.factor3_impact) && `Impact: ${humanize(ra.factor3_impact)}`]), text: prose(ra.factor3_notes) },
        { title: 'Factor 4 — The extent to which the risk has been mitigated', assessment: joinBits([has(ra.factor4_likelihood) && `Likelihood: ${humanize(ra.factor4_likelihood)}`, has(ra.factor4_impact) && `Impact: ${humanize(ra.factor4_impact)}`]), text: prose(ra.factor4_notes) },
      ].filter((f) => f.assessment !== NA || has(f.text)),
      narrative: prose(ra.narrative_summary),
    } : null,

    // Each approval reads as a sentence. No approver commentary.
    determinations: arr(inc.approvals).map((a) => {
      const decision = a.determination_approved === 'approved_breach' ? 'Determined a breach — notification required'
        : a.determination_approved === 'approved_not_breach' ? 'Determined not a breach'
          : humanize(a.determination_approved || a.decision) || 'Decision recorded';
      const who = a.approver_name || a.approver_email;
      const when = a.approved_at || a.decided_at || a.created_at;
      return `${decision}${has(who) ? `, approved by ${who}` : ''}${has(when) ? ` on ${fmtDate(when)}` : ''}.`;
    }),

    // One line per jurisdiction; internal notes stay internal.
    jurisdictions: arr(inc.affected_states).map((s) => joinBits([
      s.state_code || s.state,
      s.residents_affected != null ? `${s.residents_affected} resident${Number(s.residents_affected) === 1 ? '' : 's'} affected` : null,
      has(s.reporting_deadline_date || s.deadline_date) ? `reporting deadline ${fmtDate(s.reporting_deadline_date || s.deadline_date)}` : null,
    ])).filter((l) => l !== NA),

    obligations: arr(inc.jurisdictional_obligations).map((o) => joinBits([
      o.state_code || o.state,
      prose(o.obligation || o.requirement || o.summary),
      has(o.deadline_date || o.deadline) ? `deadline ${fmtDate(o.deadline_date || o.deadline)}` : null,
    ])).filter((l) => l !== NA),

    mitigations: arr(inc.mitigations).map((m) => ({
      title: m.title || humanize(m.action_type) || 'Mitigation action',
      text: joinBits([prose(m.description), has(m.action_date) ? `Taken ${fmtDate(m.action_date)}` : null]),
    })),

    notifications: arr(inc.notifications).map((n) => joinBits([
      humanize(n.notification_type) || 'Notification',
      has(n.sent_date) ? `sent ${fmtDate(n.sent_date)}` : null,
      n.method,
    ])).filter((l) => l !== NA),

    // Sanctions state THAT discipline was applied — type and date only.
    // The description can name an employee; that is exposure, not substance.
    sanctions: arr(inc.sanctions).map((s) => joinBits([
      humanize(s.sanction_type) || 'Sanction applied',
      has(s.applied_at) ? `applied ${fmtDate(s.applied_at)}` : null,
    ])).filter((l) => l !== NA),

    education: arr(inc.education_actions).map((e) => ({
      title: e.title || 'Education action',
      text: joinBits([prose(e.description), has(e.completed_at) ? `Completed ${fmtDate(e.completed_at)}` : null]),
    })),
  };
}

// ---------------------------------------------------------------------------
// Renderer — paragraphs under headings, in the client's colors.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// pdfSafe - every string that reaches jsPDF goes through here first.
//
// jsPDF renders pages in WinAnsi (CP1252). Any character outside that set
// is mangled into multiple Latin-1 bytes, and a mangled line renders
// letter-stretched past the page edge with its tail clipped - live-found
// 2026-08-20 on a real case file, where a single non-breaking hyphen in
// "HIPAA‑bound" broke four sections. So: tabs become spaces, control
// and zero-width characters are removed, typographic lookalikes map to
// their WinAnsi equivalents, and anything WinAnsi cannot encode is
// dropped rather than mangled. Newlines survive for splitTextToSize.
// ---------------------------------------------------------------------------
// eslint-disable-next-line no-control-regex
const PDF_STRIP = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u00ad\u200b-\u200f\u2028\u2029\ufeff]/g;
// The 27 codepoints CP1252 maps into 0x80-0x9F - the only >0xFF characters
// jsPDF can put on the page (curly quotes, dashes, bullet, ellipsis...).
const WINANSI_EXTRAS = new Set('\u20ac\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u017d\u2018\u2019\u201c\u201d\u2022\u2013\u2014\u02dc\u2122\u0161\u203a\u0153\u017e\u0178');
export const pdfSafe = (text) => [...String(text ?? '')
  .replace(/\r\n?/g, '\n')
  .replace(/\t/g, ' ')
  .replace(PDF_STRIP, '')
  .replace(/[\u2010\u2011\u2012\u2015\u2212]/g, '-')
  .replace(/[\u00a0\u1680\u2000-\u200a\u202f\u205f\u3000]/g, ' ')
  .replace(/\ufb01/g, 'fi')
  .replace(/\ufb02/g, 'fl')
  .replace(/ {3,}/g, ' ')]
  .filter((c) => c.charCodeAt(0) <= 0xff || WINANSI_EXTRAS.has(c))
  .join('');

export function renderCaseFilePdf(model) {
  const P = paletteFromBranding(model.brandTokens);



  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;
  let y = 0;

  // ── Masthead: the client's brand color carries the band. ────────────────
  doc.setFillColor(...P.accent);
  doc.rect(0, 0, pageW, 100, 'F');

  if (model.logoDataUrl) {
    // The logo sits on a white chip: many client logos are drawn in the
    // brand color itself, so painting them straight onto the brand-colored
    // band makes them invisible (orange on orange, live-found 2026-08-20).
    try {
      doc.setFillColor(...P.white);
      doc.roundedRect(pageW - MARGIN - 106, 22, 116, 42, 6, 6, 'F');
      doc.addImage(model.logoDataUrl, 'PNG', pageW - MARGIN - 96, 28, 96, 30, undefined, 'FAST');
    } catch { /* logo optional */ }
  }

  doc.setTextColor(...P.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text((model.orgName || 'PRIVACY CASE FILE').toUpperCase(), MARGIN, 36);
  doc.setFontSize(17);
  const titleLines = doc.splitTextToSize(pdfSafe(model.title), contentW - 110);
  doc.text(titleLines.slice(0, 2), MARGIN, 58);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(
    ['Privacy incident case file', model.caseRef ? `Case ${model.caseRef}` : null, model.generatedAt].filter(Boolean).join('   ·   '),
    MARGIN, 88
  );

  y = 100;

  // ── At-a-glance band. ────────────────────────────────────────────────────
  doc.setFillColor(...P.panel);
  doc.rect(0, y, pageW, 56, 'F');
  doc.setDrawColor(...P.rule);
  doc.setLineWidth(0.75);
  doc.line(0, y + 56, pageW, y + 56);

  const cellW = contentW / model.glance.length;
  model.glance.forEach((g, i) => {
    const x = MARGIN + i * cellW;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...P.accent);
    doc.text(g.label.toUpperCase(), x, y + 21);
    doc.setFontSize(10);
    doc.setTextColor(...P.ink);
    doc.text(doc.splitTextToSize(pdfSafe(g.value), cellW - 14).slice(0, 2), x, y + 35);
  });
  y += 80;

  // ── Paragraph machinery. Everything below is prose under headings. ──────
  const ensureRoom = (needed) => {
    if (y + needed > pageH - 64) { doc.addPage(); y = 60; }
  };

  const heading = (text) => {
    ensureRoom(52);
    doc.setDrawColor(...P.accent);
    doc.setLineWidth(2.5);
    doc.line(MARGIN, y, MARGIN, y + 14);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.5);
    doc.setTextColor(...P.accent);
    doc.text(text.toUpperCase(), MARGIN + 10, y + 11);
    y += 30;
  };

  // A paragraph that paginates cleanly: wrapped, chunked across pages.
  const paragraph = (text, { size = 9.5, color = P.ink, lead = 12.5, indent = 0 } = {}) => {
    // Font metrics BEFORE measuring — splitTextToSize wraps with the current
    // font, so measuring at a stale size renders lines wider than the page.
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(pdfSafe(text), contentW - indent);
    for (let i = 0; i < lines.length; i += 42) {
      const chunk = lines.slice(i, i + 42);
      ensureRoom(chunk.length * lead + 6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...color);
      doc.text(chunk, MARGIN + indent, y);
      y += chunk.length * lead;
    }
  };

  // Small bold label above a paragraph — the "Factor 1, explanation below"
  // grammar the whole document follows.
  const labelBlock = (label, text) => {
    if (!has(text)) return;
    ensureRoom(44);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...P.ink);
    const labelLines = doc.splitTextToSize(pdfSafe(label), contentW);
    doc.text(labelLines, MARGIN, y);
    y += labelLines.length * 12 + 3;
    paragraph(text);
    y += 12;
  };

  // One aligned "Label    value" line — short facts, no table chrome.
  const factLine = (label, value) => {
    ensureRoom(16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...P.inkMid);
    doc.text(`${label}:`, MARGIN, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...P.ink);
    const vLines = doc.splitTextToSize(pdfSafe(value), contentW - 250);
    doc.text(vLines, MARGIN + 246, y);
    y += Math.max(1, vLines.length) * 12.5 + 2;
  };

  const bulletLine = (text) => {
    ensureRoom(18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...P.ink);
    const lines = doc.splitTextToSize(pdfSafe(text), contentW - 14);
    doc.text('•', MARGIN, y);
    doc.text(lines, MARGIN + 14, y);
    y += lines.length * 12.5 + 4;
  };

  // ── Case overview. ───────────────────────────────────────────────────────
  if (model.narrative.length || model.keyFacts.length) {
    heading('Case overview');
    model.narrative.forEach((b) => labelBlock(b.label, b.text));
    if (model.keyFacts.length) {
      ensureRoom(20);
      y += 2;
      model.keyFacts.forEach(([l, v]) => factLine(l, v));
      y += 14;
    }
  }

  // ── Breach risk assessment: Factor N, explanation below. ────────────────
  if (model.risk) {
    heading('Breach risk assessment — 45 CFR 164.402');
    if (model.risk.conclusion.length) {
      model.risk.conclusion.forEach(([l, v]) => factLine(l, v));
      y += 12;
    }
    model.risk.factors.forEach((f) => {
      ensureRoom(56);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...P.ink);
      const tLines = doc.splitTextToSize(pdfSafe(f.title), contentW);
      doc.text(tLines, MARGIN, y);
      y += tLines.length * 12.5 + 2;
      if (f.assessment && f.assessment !== NA) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...P.accent);
        doc.text(f.assessment, MARGIN, y);
        y += 13;
      }
      if (has(f.text)) { paragraph(f.text); }
      y += 14;
    });
    if (has(model.risk.narrative)) labelBlock('Assessment summary', model.risk.narrative);
  }

  // ── Determination. ───────────────────────────────────────────────────────
  if (model.determinations.length) {
    heading('Determination');
    model.determinations.forEach((d) => bulletLine(d));
    y += 10;
  }

  // ── Jurisdictions and reporting deadlines. ──────────────────────────────
  if (model.jurisdictions.length || model.obligations.length) {
    heading('Jurisdictions and reporting deadlines');
    model.jurisdictions.forEach((l) => bulletLine(l));
    model.obligations.forEach((l) => bulletLine(l));
    y += 10;
  }

  // ── Mitigation and corrective actions. ──────────────────────────────────
  if (model.mitigations.length) {
    heading('Mitigation and corrective actions');
    model.mitigations.forEach((m) => labelBlock(m.title, m.text));
  }

  // ── Notifications. ───────────────────────────────────────────────────────
  if (model.notifications.length) {
    heading('Notifications');
    model.notifications.forEach((l) => bulletLine(l));
    y += 10;
  }

  // ── Sanctions. ───────────────────────────────────────────────────────────
  if (model.sanctions.length) {
    heading('Sanctions');
    model.sanctions.forEach((l) => bulletLine(l));
    y += 10;
  }

  // ── Education and training. ─────────────────────────────────────────────
  if (model.education.length) {
    heading('Education and training');
    model.education.forEach((e) => labelBlock(e.title, e.text));
  }

  // ── Footer on every page. ────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(...P.rule);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, pageH - 40, pageW - MARGIN, pageH - 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...P.inkSoft);
    doc.text(`${model.orgName || 'Privacy case file'}${model.caseRef ? ` · Case ${model.caseRef}` : ''} · Confidential`, MARGIN, pageH - 28);
    doc.text(`Page ${p} of ${pageCount}`, pageW - MARGIN, pageH - 28, { align: 'right' });
    doc.text('Format prepared by Precision Compliance Group', MARGIN, pageH - 18);
  }

  return doc;
}

// ---------------------------------------------------------------------------
// Entry point — gathers the client name, Deck Studio colors and logo, then
// renders and saves. Every fetch is optional: a missing logo or an
// unreachable endpoint degrades to a complete document without that element,
// never to a failed export.
// ---------------------------------------------------------------------------
export async function exportPrivacyCasePdf(incident, { apiBase = '' } = {}) {
  const API = apiBase || process.env.REACT_APP_API_URL || '';

  let orgName = '';
  try {
    const r = await fetch(`${API}/api/organizations`, { credentials: 'include' });
    if (r.ok) { const d = await r.json(); orgName = d.organizations?.[0]?.name || ''; }
  } catch { /* name optional */ }

  // The client's Deck Studio theme tokens — the same colors their decks use.
  let brandTokens = null;
  try {
    const r = await fetch(`${API}/api/deck-studio/branding`, { credentials: 'include' });
    if (r.ok) { const d = await r.json(); brandTokens = d.tokens || null; }
  } catch { /* branding optional — fallback palette renders */ }

  let logoDataUrl = null;
  try {
    const r = await fetch(`${API}/api/deck-studio/branding/logo`, { credentials: 'include' });
    if (r.ok) {
      const blob = await r.blob();
      if (blob.size > 0 && blob.type.startsWith('image/')) {
        logoDataUrl = await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        });
      }
    }
  } catch { /* logo optional */ }

  const model = buildCaseFileModel({ incident, orgName, logoDataUrl, brandTokens });
  const doc = renderCaseFilePdf(model);
  const safeTitle = (incident.title || 'privacy-case').replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 60);
  doc.save(`${safeTitle}-case-file.pdf`);
}
